import { memo, useEffect, useRef } from "react";
import { Button, Col, Container, Form, InputGroup, ListGroup, Modal, Nav, Row } from "react-bootstrap";
import { useImmer } from "use-immer";
import { Limits } from "../../constants/limits";
import useStore from "../../lib/store/useStoreService.hook";
import { LoginState } from "../../types/CommonInterfaces";
import SleepLogSettings, { CustomMetricType } from "../../types/SleepLogSettings";
import { isInteger } from "../../utils/FormatUtility";
import { StatusMessage, StatusMessageType } from "./StatusMessages";
import { SleepLogSettingsServiceContext } from "../../lib/services/sleep-log-settings-service.provider";
import SleepLogSettingsService from "../../lib/services/SleepLogSettingsService";
import SleepLogSettingsCloseConfirmation from "./SleepLogSettingsCloseConfirmation";

interface Props {
    show: boolean;
    loginState: LoginState,

    onClose: () => void;
    addStatus: (msg: StatusMessage) => any;
}

export const SleepLogSettingsModal = memo(({ show, loginState, onClose, addStatus }: Props) => {
    const [sleepLogSettingsService, initialSettings] = useStore<SleepLogSettingsService, SleepLogSettings | undefined>(SleepLogSettingsServiceContext);
    const initialSettingsRef = useRef(initialSettings);

    const [state, setState] = useImmer(() => ({
        isModified: false,
        saving: false,
        showConfirm: false,
        settings: initialSettings ?? {},
        sleepTarget:{
            hours: Math.floor((initialSettings?.targetSleepDuration ?? 0) / 60).toString(),
            mins: Math.floor((initialSettings?.targetSleepDuration ?? 0) % 60).toString() + (((initialSettings?.targetSleepDuration ?? 0) % 60) === 0 ? '0' : '')            
        },
        newMetric: {
            name: "",
            type: "0_to_10" as CustomMetricType,
            options: [] as string[],
            optionInput: "",
            optionError: "",
            show: false            
        },
        newAutoTag: "",
        newAutoTreatment: "",
        newAutoMedication: "",
        newAutoOtherMedication: "",
        settingsTab: "general" as "general" | "auto-add" | "custom-values" | "notes",
        newDefaultType: "tag" as "tag" | "treatment" | "sleepMed" | "otherMed",
        newDefaultValue: "",
    }));

    useEffect(() => {
        if (initialSettingsRef.current === undefined && initialSettings) {
            setState(state => {
                state.settings = initialSettings;
                state.sleepTarget = {
                    hours: Math.floor((initialSettings?.targetSleepDuration ?? 0) / 60).toString(),
                    mins: Math.floor((initialSettings?.targetSleepDuration ?? 0) % 60).toString() + (((initialSettings?.targetSleepDuration ?? 0) % 60) === 0 ? '0' : '')            
                };
            });

            initialSettingsRef.current = initialSettings;
        }
    }, [initialSettings, setState]);

    function changeUseNotesTemplate(e: any) {
        setState(state => {
            state.settings.useNotesTemplate = e.target.checked;
            state.isModified = true;
        });
    }

    function changeNotesTemplate(e: any) {
        setState(state => {
            state.settings.notesTemplate = e.target.value;
            state.isModified = true;
        });
    }

    function changeNotesPrompt(e: any) {
        setState(state => {
            state.settings.notesPrompt = e.target.value;
            state.isModified = true;
        });
    }

    function changeUserInfoForChat(e: any) {
        setState(state => {
            state.settings.userInfoForChat = e.target.value;
            state.isModified = true;
        });
    }

    function updateTargetSleepHours(e: any) {

        if (e.target.value.length > 0 && !isInteger(e.target.value)) {
            return;
        }

        if (e.target.value.length >= 3) {
            return;
        }

        const hours = e.target.value;

        setState(state => {
            state.sleepTarget.hours = hours;
            state.isModified = true;
        });
    }

    function updateTargetSleepMins(e: any) {
        if (e.target.value.length > 0 && !isInteger(e.target.value)) {
            return;
        }

        if (e.target.value.length >= 3) {
            return;
        }

        const mins = e.target.value;

        setState(state => {
            state.sleepTarget.mins = mins;
            state.isModified = true;
        });      
    }    

    const addSelectOption = () => {
        const option = state.newMetric.optionInput.trim();
        let error = "";
        if (!option) {
            error = "Enter an option.";
        } else if (option.length > 128) {
            error = "Options must be 128 characters or fewer.";
        } else if (state.newMetric.options.includes(option)) {
            error = "This option is already in the list.";
        } else if (state.newMetric.options.length >= 32) {
            error = "You can add up to 32 options.";
        }
        setState(state => {
            state.newMetric.optionError = error;
            if (!error) {
                state.newMetric.options.push(option);
                state.newMetric.optionInput = "";
            }
        });
    };

    const addNewMetric = () => {
        const name = state.newMetric.name.trim();
        const options = state.newMetric.options;
        if (!name) {
            addStatus({ type: StatusMessageType.Fail, msg: "A custom value name cannot be empty." });
            return;
        }
        
        if (state.settings.customMetrics?.some(metric => metric.name.trim() === name)) {
            addStatus({type: StatusMessageType.Fail, msg: "A custom value with the same name already exists."});
            return;
        }

        if (state.newMetric.type === "single_select" && state.newMetric.optionInput.trim()) {
            addStatus({ type: StatusMessageType.Fail, msg: "Add or clear the pending option before adding this custom value." });
            return;
        }
        if (state.newMetric.type === "single_select" &&
            (options.length === 0 || options.length > 32 || options.some(option => option.length > 128) || new Set(options).size !== options.length)) {
            addStatus({ type: StatusMessageType.Fail, msg: "Add 1 to 32 unique options, with at most 128 characters each." });
            return;
        }

        setState(state => {
            if (!state.settings.customMetrics) {
                state.settings.customMetrics = [];
            }

            state.settings.customMetrics.push(state.newMetric.type === "single_select"
                ? { name, type: "single_select", options }
                : { name, type: state.newMetric.type });

            state.newMetric = {
                name: "",
                type: "0_to_10",
                options: [],
                optionInput: "",
                optionError: "",
                show: false
            };

            state.isModified = true;
        });
    }

    const deleteMetric = (index: number) => {
        setState(state => {
            state.settings.customMetrics?.splice(index, 1);
            state.isModified = true;
        });
    }

    async function handleSaveSettings() {
        if (loginState.loggedIn) {

            setState(state => {
                state.saving = true;
            });

            const sleepTargetInMins = (Number(state.sleepTarget.hours) * 60) + Number(state.sleepTarget.mins);
            const settingsToPersist = { ...state.settings, targetSleepDuration: sleepTargetInMins };
            let succeeded = false;

            try {
                await sleepLogSettingsService.save(settingsToPersist);
                addStatus({ type: StatusMessageType.Success, msg: "Settings saved." });
                succeeded = true;
            }
            catch (e: any) {
                addStatus({ type: StatusMessageType.Fail, msg: "Saving settings failed. Please try again later." });
            }

            setState(state => {
                state.saving = false;

                if (succeeded) {
                    state.isModified = false;
                }
            });
        }
    }

    function confirmClose() {
        if (state.isModified) {
            setState(state => {
                state.showConfirm = true;
            });
        }
        else {
            onClose();
        }
    }

    const displayMetricType = (type: CustomMetricType) => {
        switch (type) {
            case "0_to_10":
                return "0 to 10 scale";
            case "1_to_5":
                return "1 to 5 scale";
            case "number":
                return "number";
            case "text":
                return "Text";
            case "single_select":
                return "Single select";
            default:
                return "Unknown type";
        }
    };

    const disableAddMetric = () => (state.settings.customMetrics?.length ?? 0) >= 25;
    const maxAutoTags = state.settings.maxTags ?? Limits.MaxTags;
    const deleteAutoTag = (index: number) => {
        setState(state => {
            state.settings.autoTags?.splice(index, 1);
            state.isModified = true;
        });
    };

    const maxAutoItems = 32;

    const deleteAutoTreatment = (index: number) => {
        setState(state => { state.settings.autoTreatments?.splice(index, 1); state.isModified = true; });
    };

    const deleteAutoMedication = (index: number) => {
        setState(state => { state.settings.autoMedications?.splice(index, 1); state.isModified = true; });
    };

    const deleteAutoOtherMedication = (index: number) => {
        setState(state => { state.settings.autoOtherMedications?.splice(index, 1); state.isModified = true; });
    };

    const totalDefaultItems =
        (state.settings.autoTags?.length ?? 0) +
        (state.settings.autoTreatments?.length ?? 0) +
        (state.settings.autoMedications?.length ?? 0) +
        (state.settings.autoOtherMedications?.length ?? 0);

    const addDefault = () => {
        const value = state.newDefaultValue.trim();
        if (!value) {
            addStatus({ type: StatusMessageType.Fail, msg: "Name cannot be empty." });
            return;
        }
        if (value.length > Limits.MaxTagsLength) {
            addStatus({ type: StatusMessageType.Fail, msg: `Name can only contain at most ${Limits.MaxTagsLength} characters.` });
            return;
        }

        const type = state.newDefaultType;

        if (type === "tag") {
            if (state.settings.autoTags?.includes(value)) {
                addStatus({ type: StatusMessageType.Fail, msg: "This tag is already in the defaults." }); return;
            }
            if ((state.settings.autoTags?.length ?? 0) >= maxAutoTags) {
                addStatus({ type: StatusMessageType.Fail, msg: `Only ${maxAutoTags} default tags are allowed.` }); return;
            }
            setState(s => {
                if (!s.settings.autoTags) { s.settings.autoTags = []; }
                s.settings.autoTags.push(value);
                s.newDefaultValue = "";
                s.isModified = true;
            });
        } else if (type === "treatment") {
            if (state.settings.autoTreatments?.includes(value)) {
                addStatus({ type: StatusMessageType.Fail, msg: "This treatment is already in the defaults." }); return;
            }
            if ((state.settings.autoTreatments?.length ?? 0) >= maxAutoItems) {
                addStatus({ type: StatusMessageType.Fail, msg: `Only ${maxAutoItems} default treatments are allowed.` }); return;
            }
            setState(s => {
                if (!s.settings.autoTreatments) { s.settings.autoTreatments = []; }
                s.settings.autoTreatments.push(value);
                s.newDefaultValue = "";
                s.isModified = true;
            });
        } else if (type === "sleepMed") {
            if (state.settings.autoMedications?.some(m => m.name === value)) {
                addStatus({ type: StatusMessageType.Fail, msg: "This sleep medication is already in the defaults." }); return;
            }
            if ((state.settings.autoMedications?.length ?? 0) >= maxAutoItems) {
                addStatus({ type: StatusMessageType.Fail, msg: `Only ${maxAutoItems} default sleep medications are allowed.` }); return;
            }
            setState(s => {
                if (!s.settings.autoMedications) { s.settings.autoMedications = []; }
                s.settings.autoMedications.push({ name: value });
                s.newDefaultValue = "";
                s.isModified = true;
            });
        } else if (type === "otherMed") {
            if (state.settings.autoOtherMedications?.some(m => m.name === value)) {
                addStatus({ type: StatusMessageType.Fail, msg: "This other medication is already in the defaults." }); return;
            }
            if ((state.settings.autoOtherMedications?.length ?? 0) >= maxAutoItems) {
                addStatus({ type: StatusMessageType.Fail, msg: `Only ${maxAutoItems} default other medications are allowed.` }); return;
            }
            setState(s => {
                if (!s.settings.autoOtherMedications) { s.settings.autoOtherMedications = []; }
                s.settings.autoOtherMedications.push({ name: value });
                s.newDefaultValue = "";
                s.isModified = true;
            });
        }
    };

    return (
    <>
        <Modal 
            scrollable={true} 
            show={show}
            onHide={confirmClose}
            fullscreen="lg-down"
            size="lg"
            dialogClassName="sleep-log-modal"
        >
            <Modal.Header style={{ paddingTop: "1.5rem" }}>
                <Modal.Title>Settings</Modal.Title>
                <button type="button" className="btn-close ms-auto" aria-label="Close" onClick={confirmClose}></button>
            </Modal.Header>
            <Modal.Body className="px-0" style={{scrollbarGutter: "stable", scrollbarWidth: "thin"}}>
                {!initialSettings &&
                    <div className="d-flex justify-content-center mt-5" style={{gap: "1rem"}}>
                        <span className="spinner-grow text-secondary" role="status" aria-hidden="true"></span>
                        <span className="spinner-grow text-secondary" role="status" aria-hidden="true"></span>
                        <span className="spinner-grow text-secondary" role="status" aria-hidden="true"></span>
                    </div>                        
                }
                {initialSettings &&
                    <Container>
                        <Nav
                            variant="underline"
                            className="mb-4"
                            activeKey={state.settingsTab}
                            onSelect={(key) => setState(state => { state.settingsTab = (key ?? "general") as any; })}
                        >
                            <Nav.Item>
                                <Nav.Link eventKey="general">General</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="custom-values">Custom Values</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="auto-add">Default Values</Nav.Link>
                            </Nav.Item>
                        </Nav>

                        {state.settingsTab === "general" && <>
                        <div className="border-bottom pb-4"> 
                            <div className="fw-semibold mb-2">
                                Sleep Target
                            </div>
                            <div className="mt-2">
                                <input type="number" 
                                    className="underline-input bg-transparent"
                                    style={{width: "1.5rem"}}
                                    value={state.sleepTarget.hours}
                                    onChange={updateTargetSleepHours}
                                    onBlur={() => setState(state => { 
                                        state.sleepTarget.hours = state.sleepTarget.hours ? state.sleepTarget.hours : '0';
                                        state.isModified = true;
                                    })}
                                >
                                </input>
                                <span> : </span>
                                <input type="number"
                                    className="underline-input bg-transparent"
                                    style={{width: "1.5rem"}}
                                    value={state.sleepTarget.mins}
                                    onChange={updateTargetSleepMins}
                                    onBlur={() => setState(state => { 
                                        state.sleepTarget.mins = state.sleepTarget.mins ? state.sleepTarget.mins : '00';
                                        state.isModified = true;
                                    })}
                                >
                                </input>
                            </div>
                        </div>

                        <div className="border-bottom py-4"> 
                            <div className="fw-semibold mb-2">
                                Target Bedtime
                            </div>
                            <div className="mt-2">
                                <input type="time"
                                    className="form-control w-auto"
                                    value={state.settings.targetBedTime}
                                    onChange={e => setState(state => {
                                        state.settings.targetBedTime = e.target.value;
                                        state.isModified = true;
                                    })}
                                >
                                </input>
                            </div>                            
                        </div>

                        <div className="border-bottom py-4"> 
                            <div className="fw-semibold mb-2">
                                Target Wake Up
                            </div>
                            <div className="mt-2">
                                <input type="time"
                                    className="form-control w-auto"
                                    value={state.settings.targetWakeTime}
                                    onChange={e => setState(state => { 
                                        state.settings.targetWakeTime = e.target.value; 
                                        state.isModified = true;
                                    })}
                                >
                                </input>
                            </div>                            
                        </div>

                        <Row className="pt-4 pb-2">
                            <Col>
                                <div className="fw-semibold mb-2">
                                    Use Notes Template
                                </div>
                            </Col>
                            <Col>
                                <div className="form-check">
                                    <input className="form-check-input" type="checkbox" id="filter-analytics-no-sleep-med"
                                    onChange={changeUseNotesTemplate} checked={state.settings.useNotesTemplate}/>
                                </div>                            
                            </Col>                            
                        </Row>

                        <Row className="border-bottom py-4">
                            <Col xs="12">
                                <div className="fw-semibold mb-2">
                                    Notes Template
                                </div>
                            </Col>
                            <Col>    
                                <textarea 
                                    className="form-control border-rounded " 
                                    rows={10} 
                                    value={state.settings.notesTemplate ?? ""}
                                    placeholder="Add a notes template. This will be used for any newly created logs."
                                    onChange={changeNotesTemplate} maxLength={Limits.MaxNotesLength} style={{}}>
                                </textarea>                                             
                            </Col>  
                        </Row>
                        <Row className="py-4">
                            <Col xs="12">
                                <div className="fw-semibold mb-2">
                                    Notes Prompt
                                </div>
                            </Col>
                            <Col>    
                                <textarea 
                                    className="form-control border-rounded "
                                    rows={10}
                                    value={state.settings.notesPrompt ?? ""}
                                    placeholder="Add a notes prompt."
                                    onChange={changeNotesPrompt} maxLength={Limits.MaxNotesLength} style={{}}>
                                </textarea>                                             
                            </Col>
                        </Row>
                        <Row className="pt-2 pb-4">
                            <Col xs="12">
                                <div className="fw-semibold mb-2">
                                    Additional info
                                </div>
                            </Col>
                            <Col>
                                <textarea
                                    className="form-control border-rounded "
                                    rows={5}
                                    value={state.settings.userInfoForChat ?? ""}
                                    placeholder="Add a bio to improve responses for summaries and asking questions about your sleep diaries."
                                    onChange={changeUserInfoForChat}
                                    maxLength={Limits.MaxNotesLength}
                                >
                                </textarea>
                            </Col>
                        </Row>                        
                        </>}

                        {state.settingsTab === "auto-add" && <>
                        <div className="py-2">
                            <div className="text-muted small">
                                These values will be automatically added to new sleep diary entries.
                            </div>
                        </div>

                        <ListGroup className="mt-3">
                            <ListGroup.Item className="d-flex bg-light fw-bold" style={{justifyContent: "space-between"}}>
                                <div style={{flex: "5 1 0"}}>Name</div>
                                <div style={{flex: "4 1 0"}}>Type</div>
                                <div style={{flex: "2 1 0"}}>Actions</div>
                            </ListGroup.Item>

                            {state.settings.autoTags?.map((tag, i) =>
                                <ListGroup.Item key={`tag-${tag}`} className="d-flex" style={{justifyContent: "space-between"}}>
                                    <div style={{flex: "5 1 0"}}>{tag}</div>
                                    <div style={{flex: "4 1 0"}}><span className="badge bg-primary bg-opacity-10 text-primary">Tag</span></div>
                                    <div style={{flex: "2 1 0"}}>
                                        <Button variant="outline-danger" size="sm" onClick={() => deleteAutoTag(i)}>
                                            <i className="bi bi-trash3"></i>
                                        </Button>
                                    </div>
                                </ListGroup.Item>
                            )}
                            {state.settings.autoTreatments?.map((treatment, i) =>
                                <ListGroup.Item key={`treatment-${treatment}`} className="d-flex" style={{justifyContent: "space-between"}}>
                                    <div style={{flex: "5 1 0"}}>{treatment}</div>
                                    <div style={{flex: "4 1 0"}}><span className="badge bg-success bg-opacity-10 text-success">Treatment</span></div>
                                    <div style={{flex: "2 1 0"}}>
                                        <Button variant="outline-danger" size="sm" onClick={() => deleteAutoTreatment(i)}>
                                            <i className="bi bi-trash3"></i>
                                        </Button>
                                    </div>
                                </ListGroup.Item>
                            )}
                            {state.settings.autoMedications?.map((med, i) =>
                                <ListGroup.Item key={`med-${med.name}`} className="d-flex" style={{justifyContent: "space-between"}}>
                                    <div style={{flex: "5 1 0"}}>{med.name}</div>
                                    <div style={{flex: "4 1 0"}}><span className="badge bg-info bg-opacity-10 text-info">Sleep Medication</span></div>
                                    <div style={{flex: "2 1 0"}}>
                                        <Button variant="outline-danger" size="sm" onClick={() => deleteAutoMedication(i)}>
                                            <i className="bi bi-trash3"></i>
                                        </Button>
                                    </div>
                                </ListGroup.Item>
                            )}
                            {state.settings.autoOtherMedications?.map((med, i) =>
                                <ListGroup.Item key={`othermed-${med.name}`} className="d-flex" style={{justifyContent: "space-between"}}>
                                    <div style={{flex: "5 1 0"}}>{med.name}</div>
                                    <div style={{flex: "4 1 0"}}><span className="badge bg-warning bg-opacity-10 text-warning">Other Medication</span></div>
                                    <div style={{flex: "2 1 0"}}>
                                        <Button variant="outline-danger" size="sm" onClick={() => deleteAutoOtherMedication(i)}>
                                            <i className="bi bi-trash3"></i>
                                        </Button>
                                    </div>
                                </ListGroup.Item>
                            )}
                        </ListGroup>

                        {totalDefaultItems === 0 &&
                            <div className="text-muted text-center small mt-2">No defaults configured. Add one below.</div>
                        }

                        <div className="pt-4">
                            <div className="fw-medium">Add</div>
                            <div className="p-2"></div>
                            <InputGroup>
                                <Form.Select
                                    style={{flex: "0 0 auto", width: "auto"}}
                                    value={state.newDefaultType}
                                    onChange={e => setState(state => { state.newDefaultType = e.target.value as any; })}
                                >
                                    <option value="tag">Tag</option>
                                    <option value="treatment">Treatment</option>
                                    <option value="sleepMed">Sleep Medication</option>
                                    <option value="otherMed">Other Medication</option>
                                </Form.Select>
                                <Form.Control
                                    type="text"
                                    maxLength={Limits.MaxTagsLength}
                                    placeholder="Enter name"
                                    value={state.newDefaultValue}
                                    onKeyDown={e => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            addDefault();
                                        }
                                    }}
                                    onChange={e => setState(state => {
                                        state.newDefaultValue = e.target.value;
                                    })}
                                />
                                <Button
                                    variant="outline-primary"
                                    onClick={addDefault}
                                >
                                    Add
                                </Button>
                            </InputGroup>
                        </div>
                        </>}

                        {state.settingsTab === "custom-values" && <>
                        <div className="border-bottom py-4">
                            <div className="fw-semibold">
                                Custom Values
                            </div>
                            <div className=" py-2">Deleting custom values keeps recorded values in existing sleep logs, but removes their available options.</div>
                            <ListGroup>
                                <ListGroup.Item className="d-flex  bg-light fw-bold" style={{justifyContent: "space-between"}}>
                                    <div style={{flex: "6 1 0"}}>Name</div>
                                    <div style={{flex: "6 1 0"}}>Type</div>
                                    <div style={{flex: "2 1 0"}}>Actions</div>
                                </ListGroup.Item>
                                { state.settings?.customMetrics?.map((metric, i) =>
                                    <ListGroup.Item key={metric.name} className="d-flex " style={{justifyContent: "space-between"}}>
                                        <div style={{flex: "6 1 0"}}>{metric.name}</div>
                                        <div style={{flex: "6 1 0", minWidth: 0, overflowWrap: "anywhere"}}>
                                            {displayMetricType(metric.type)}
                                            {metric.type === "single_select" && <div className="small text-muted">{metric.options.join(", ")}</div>}
                                        </div>
                                        <div style={{flex: "2 1 0"}}>
                                            <Button variant="outline-danger"
                                                size="sm" 
                                                onClick={() => deleteMetric(i)}
                                            >
                                                <i className="bi bi-trash3"></i>
                                            </Button>
                                        </div>
                                    </ListGroup.Item>
                                )}
                                
                            </ListGroup>
                            { !state.newMetric.show &&
                                <div className="text-center mt-3">
                                    <Button variant="secondary rounded-5"
                                        onClick={() => setState(state => { state.newMetric.show = true; })}
                                    >
                                        + Add New Value
                                    </Button>
                                </div>
                            }
                            { state.newMetric.show &&
                                <div className="pt-4">
                                    <div className="fw-medium">Add new value</div>
                                    <div className="p-2"></div>
                                    <InputGroup>
                                        <InputGroup.Text>Name</InputGroup.Text>
                                        <Form.Control 
                                            type="text"
                                            aria-label="Custom value name"
                                            maxLength={100}
                                            disabled={disableAddMetric()}
                                            placeholder="Add name"
                                            value={state.newMetric.name}
                                            onChange={e => setState(state => { 
                                                state.newMetric.name = e.target.value;
                                            })}
                                            autoFocus
                                        />
                                    </InputGroup>

                                    <div className="p-2"></div>

                                    <InputGroup className="">
                                        <InputGroup.Text>Type &nbsp;</InputGroup.Text>
                                        <Form.Select 
                                            aria-label="Custom value type"
                                            value={state.newMetric.type}
                                            disabled={disableAddMetric()} 
                                            onChange={e => setState(state => { 
                                                state.newMetric.type = e.target.value as CustomMetricType;
                                            })}
                                        >
                                            <option value="0_to_10">{displayMetricType("0_to_10")}</option>
                                            <option value="1_to_5">{displayMetricType("1_to_5")}</option>
                                            <option value="number">{displayMetricType("number")}</option>
                                            <option value="text">{displayMetricType("text")}</option>
                                            <option value="single_select">{displayMetricType("single_select")}</option>
                                        </Form.Select>
                                    </InputGroup>

                                    {state.newMetric.type === "single_select" &&
                                        <Form.Group className="mt-3" controlId="custom-value-option">
                                            <Form.Label>Allowed options</Form.Label>
                                            <InputGroup style={{ maxWidth: "28rem" }}>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Add an option"
                                                    maxLength={128}
                                                    value={state.newMetric.optionInput}
                                                    disabled={disableAddMetric() || state.newMetric.options.length >= 32}
                                                    isInvalid={!!state.newMetric.optionError}
                                                    aria-describedby="custom-value-options-help custom-value-options-error"
                                                    onChange={e => setState(state => {
                                                        state.newMetric.optionInput = e.target.value;
                                                        state.newMetric.optionError = "";
                                                    })}
                                                    onKeyDown={e => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            addSelectOption();
                                                        }
                                                    }}
                                                />
                                                <Button
                                                    variant="outline-primary"
                                                    disabled={disableAddMetric() || !state.newMetric.optionInput.trim() || state.newMetric.options.length >= 32}
                                                    onClick={addSelectOption}
                                                >
                                                    Add option
                                                </Button>
                                            </InputGroup>

                                            {state.newMetric.optionError &&
                                                <div id="custom-value-options-error" role="alert" className="text-danger small">{state.newMetric.optionError}</div>
                                            }
                                            {state.newMetric.options.length > 0 &&
                                                <ListGroup as="ul" className="mt-2 overflow-auto" aria-label="Configured options" style={{ maxWidth: "28rem", maxHeight: "14rem" }}>
                                                    {state.newMetric.options.map((option, index) =>
                                                        <ListGroup.Item as="li" key={option} className="d-flex align-items-center gap-2 py-1">
                                                            <span className="text-truncate flex-grow-1" style={{ minWidth: 0 }} title={option}>{option}</span>
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                className="flex-shrink-0"
                                                                aria-label={`Remove option ${option}`}
                                                                disabled={disableAddMetric()}
                                                                onClick={() => setState(state => {
                                                                    state.newMetric.options.splice(index, 1);
                                                                    state.newMetric.optionError = "";
                                                                })}
                                                            >
                                                                <i className="bi bi-x-lg" aria-hidden="true"></i>
                                                            </Button>
                                                        </ListGroup.Item>
                                                    )}
                                                </ListGroup>
                                            }
                                        </Form.Group>
                                    }

                                    <div className="p-3 d-flex gap-4 justify-content-center">
                                        <Button 
                                            variant="primary"
                                            style={{"WebkitTextStrokeWidth": ".5px"}}
                                            disabled={disableAddMetric()} 
                                            onClick={addNewMetric}
                                        >
                                            Add
                                        </Button>
                                        <Button 
                                            variant="outline-secondary"
                                            style={{"WebkitTextStrokeWidth": ".5px"}}
                                            onClick={() => setState(state => { state.newMetric.show = false; })}
                                        >
                                            Cancel
                                        </Button>
                                    </div>                                  
                                </div>
                            }
                        </div>
                        </>}
                    </Container>
                }
            </Modal.Body>
            <Modal.Footer className="justify-content-center">
                <button type="button"
                    className="btn btn-primary px-4 rounded-pill" 
                    onClick={handleSaveSettings}
                    disabled={!initialSettings || !state.isModified || state.saving}
                >
                    {!state.saving ?
                        "Save" :
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    }
                </button>
            </Modal.Footer>
        </Modal>

        { state.showConfirm && 
            <SleepLogSettingsCloseConfirmation
                show={true} 
                close={() => setState(state => ({...state, showConfirm: false }))} 
                confirm={() => {
                    setState(state => ({...state, showConfirm: false }));
                    onClose();
                }} 
            />
        }
    </>    
    )
});
